import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CustomLoader from "./CustomLoader";
import { TextInput, EmailInput, Dropdown } from "./FormInputs";
import OverrideRoleDrawer from "./OverrideRoleDrawer";
import UserAssignedClients from "./UserAssignedClients";
import CircularProgress from "@mui/material/CircularProgress";
import { RefreshCw } from "lucide-react";
import useToast from "./Toast/useToast";
import { toDesignationList } from "../utils/designationFilters";
import ValidationMessage from "./Validation";
import { emailRegex } from "../helper/regex";
import "../App.css";
import AppButton from "./AppButton";
import { getCurrentUserId } from "../helper/commonFunctions";
import ConfirmActionModal from "./ConfirmActionModal";
import { ApiCall } from "../helper/axios";
import { selectToken } from "../redux/authSlice";
import { useSelector, useDispatch } from "react-redux";
import { checkUserEmailExists } from "../redux/userDataSlice";
const drawerWidth = 600;
const transformUserPermissions = (userPermissions) => {
    const transformed = {};
    Object.keys(userPermissions).forEach((moduleKey) => {
        const perm = userPermissions[moduleKey];
        transformed[moduleKey] = {
            access: perm.access !== undefined ? perm.access : false,
            actions: Array.isArray(perm.actions) ? perm.actions : [],
            client_scope: perm.client_scope || "assigned",
            refresh: perm.refresh !== undefined ? perm.refresh : false,
        };
    });
    return transformed;
};
// Transform user overrides from API format to ModulePermission format
const transformUserOverrides = (userOverrides) => {
    const transformed = {};
    Object.keys(userOverrides).forEach((moduleKey) => {
        const override = userOverrides[moduleKey];
        transformed[moduleKey] = {
            access: override.access !== undefined ? override.access : false,
            actions: Array.isArray(override.actions) ? override.actions : [],
            client_scope: override.client_scope || "assigned",
            refresh: override.refresh !== undefined ? override.refresh : false,
        };
    });
    return transformed;
};
const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" }
];
const locationOptions = [
    { value: "US", label: "USA" },
    { value: "IN", label: "India" }
];
const twoFactorAuth = [
    { value: "true", label: 'ON' },
    { value: "false", label: 'OFF' }
];
// Validation schema using Yup
const getValidationSchema = (isEditMode) => {
    return Yup.object({
        firstName: Yup.string()
            .trim()
            .required(ValidationMessage.firstNameRequired),
        lastName: Yup.string()
            .trim()
            .required(ValidationMessage.lastNameRequired),
        email: Yup.string()
            .trim()
            .required(ValidationMessage.emailRequired)
            .matches(emailRegex, ValidationMessage.emailInvalid)
            .email(ValidationMessage.emailInvalid),
        role: Yup.string()
            .required(ValidationMessage.roleRequired),
        // One or more designations.
        user_type: Yup.array().of(Yup.string())
            .min(1, ValidationMessage.designationRequired),
        status: Yup.string()
            .required(ValidationMessage.statusRequired),
        two_step_enabled: Yup.string()
            .required(ValidationMessage.two_factor_authentication),
        location: Yup.string()
            .required(ValidationMessage.locationRequired),
        employeePortfolio: Yup.array()
            .optional(),
        avatar: Yup.mixed()
            .nullable()
            .test("fileSize", "File size exceeds the limit. Please upload a smaller image.", (file) => typeof file === "string" ? true : !file || file.size <= 7 * 1024 * 1024)
            .test("fileType", "File type must be .jpeg, .jpg, .gif, .webp, or .png", (file) => typeof file === "string" ? true : !file || ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', "image/webp"].includes(file.type)),
    });
};
function UserDrawer({ open, onClose, userData = null, onSave, roleOptions = [], employeePortfolioOptions = [], designationOptions = [], onOverrideSave }) {
    const isEditMode = !!userData;
    const [avatarInitials, setAvatarInitials] = useState(null);
    const [overrideDrawerOpen, setOverrideDrawerOpen] = useState(false);
    const [hasOverrides, setHasOverrides] = useState(false); // Track if user has overrides
    const [previousRole, setPreviousRole] = useState(null);
    const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);
    const [pendingRole, setPendingRole] = useState(null);
    const [emailError, setEmailError] = useState("");
    const token = useSelector(selectToken);
    const dispatch = useDispatch();
    const toast = useToast();
    // Clockify ID: shown read-only when the user already has one; otherwise the refresh button next to the field
    // asks the server to match this user's email with their Clockify account and save the id.
    const [clockifyId, setClockifyId] = useState("");
    const [clockifySyncing, setClockifySyncing] = useState(false);
    useEffect(() => {
        setClockifyId(userData?.clockify_user_id || "");
    }, [userData?.id, userData?._id, userData?.clockify_user_id]);
    const handleSyncClockifyId = async () => {
        const id = userData?.id || userData?._id;
        if (!id || clockifySyncing)
            return;
        setClockifySyncing(true);
        try {
            const res = await ApiCall("POST", `/api/users/${id}/sync-clockify-id`, {}, { Authorization: token });
            const body = res?.data ?? res?.response?.data;
            if (body?.success && body?.data?.clockify_user_id) {
                setClockifyId(body.data.clockify_user_id);
                toast.success("Clockify ID synced.");
            }
            else {
                toast.error(body?.message || "Could not sync the Clockify ID.");
            }
        }
        catch {
            toast.error("Could not sync the Clockify ID. Please try again.");
        }
        finally {
            setClockifySyncing(false);
        }
    };
    const getRolePermissionsById = (roleId) => {
        // Always use pure role permissions (not user's merged permissions) so the diff
        // comparison in OverrideRoleDrawer correctly identifies what differs from the role.
        // roleOptions is always fresh — fetchRoles() is called before opening the drawer.
        const role = roleOptions.find(r => r.value === roleId);
        return role?.permissions || {};
    };
    // Get role name from role ID
    const getRoleName = (roleId) => {
        if (!roleId)
            return "";
        // Priority 1: Check if userData has role name (edit mode)
        // This handles case when userData.role.name exists
        if (isEditMode && userData?.role?.name) {
            return userData.role.name;
        }
        // Priority 2: Find from roleOptions (works for both add and edit mode)
        const roleOption = roleOptions.find(option => option.value === roleId);
        if (roleOption?.label) {
            return roleOption.label;
        }
        // Fallback: return empty string
        return "";
    };
    // Check if user has overrides when userData changes
    useEffect(() => {
        if (userData) {
            const overrides = userData?.overrides || {};
            const hasOverridesData = Object.keys(overrides).length > 0;
            setHasOverrides(hasOverridesData);
        }
        else {
            setHasOverrides(false);
        }
    }, [userData]);
    useEffect(() => {
        if (isEditMode && userData) {
            setPreviousRole(userData.role_id);
        }
    }, [userData, isEditMode]);
    const formik = useFormik({
        initialValues: {
            firstName: userData?.first_name || "",
            lastName: userData?.last_name || "",
            email: userData?.email || "",
            // Store role id in formik; show label via roleOptions
            role: userData?.role_id || "",
            // An older user has one designation as a plain string; a user with several has an array.
            user_type: toDesignationList(userData?.user_type),
            status: userData?.status || "Active",
            two_step_enabled: userData?.two_step_enabled || userData?.two_step_enabled === false ? JSON.stringify(userData?.two_step_enabled) : "",
            is_meeting_am: userData?.is_meeting_am === true,
            location: userData?.location || "",
            employeePortfolio: Array.isArray(userData?.employee_portfolio)
                ? userData.employee_portfolio.map((u) => u._id)
                : [],
            avatar: userData?.profile_pic || null,
        },
        enableReinitialize: true,
        validationSchema: getValidationSchema(isEditMode),
        onSubmit: async (values, { resetForm, setSubmitting, setFieldError }) => {
            try {
                // Email backend check before submitting
                if (values.email) {
                    const _id = isEditMode ? userData?.id || userData?._id : undefined;
                    try {
                        const result = await dispatch(checkUserEmailExists({ email: values.email, _id })).unwrap();
                        if (result?.exists) {
                            setEmailError(result?.message || "This email is already exists.");
                            setSubmitting(false);
                            return;
                        }
                    }
                    catch (e) {
                        console.error("Check email error", e);
                    }
                }
                if (onSave) {
                    const ok = await onSave(values);
                    // On create success: reset form but keep drawer open (as requested)
                    if (ok && !isEditMode) {
                        resetForm();
                        setAvatarInitials(null);
                    }
                }
            }
            catch (e) {
                console.error('save user error', e);
            }
            finally {
                setSubmitting(false);
            }
        },
    });
    const handleEmailValidation = async (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            return;
        }
        const _id = isEditMode ? userData?.id || userData?._id : undefined;
        try {
            const result = await dispatch(checkUserEmailExists({ email, _id })).unwrap();
            if (result?.exists) {
                setEmailError(result?.message || "This email is already exists.");
            }
            else {
                setEmailError("");
            }
        }
        catch (e) {
            console.error("Check email error", e);
        }
    };
    const handleChange = (name, e) => {
        const value = e?.target?.value || '';
        formik.setFieldValue(name, value);
        if (name === 'email') {
            setEmailError("");
        }
    };
    const handleBlur = (name) => {
        formik.setFieldTouched(name, true);
        if (name === 'email') {
            handleEmailValidation(formik.values.email);
        }
    };
    const handleAvatarUpload = (e, setFieldError) => {
        const reader = new FileReader(), files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const fileSizeMB = file.size / (1024 * 1024);
            const fileType = file.type.split('/')[0];
            const maxSize = 7;
            const allowedTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', "image/webp"];
            if (!allowedTypes.includes(file.type)) {
                setFieldError('avatar', "File type must be .jpeg, .jpg, .gif, .webp, or .png");
                e.target.value = '';
                return;
            }
            if (fileSizeMB > maxSize) {
                setFieldError('avatar', `File size exceeds the limit of ${maxSize} MB for ${fileType} files.`);
                e.target.value = '';
                return;
            }
            reader.onload = async function ({ target }) {
                setAvatarInitials(target?.result || null);
                formik.setFieldValue('avatar', files[0]);
            };
            reader.readAsDataURL(files[0]);
        }
    };
    const getAvatarSrc = (avatar) => {
        if (!avatar)
            return undefined;
        // API se aayi base64 / url string
        if (typeof avatar === "string") {
            return avatar;
        }
        // New upload (File)
        if (avatar instanceof File) {
            return URL.createObjectURL(avatar);
        }
        return undefined;
    };
    const handleRoleChange = (name, e) => {
        const newRoleId = e.target.value;
        if (hasOverrides && isEditMode && newRoleId !== formik.values.role) {
            setPendingRole(newRoleId);
            setShowRoleChangeModal(true);
            return;
        }
        formik.setFieldValue(name, newRoleId);
        const newRolePermissions = getRolePermissionsById(newRoleId);
        if (userData) {
            userData.permissions = newRolePermissions;
            userData.overrides = {};
        }
        setHasOverrides(false);
        if (onOverrideSave) {
            onOverrideSave({});
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Drawer, { anchor: "right", className: "user-list-edit", open: open, onClose: onClose, sx: {
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }, children: _jsx(FormikProvider, { value: formik, children: _jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            backgroundColor: '#fdfdfd'
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    height: '60px',
                                    borderBottom: '1px solid #e9eaeb',
                                    backgroundColor: '#fafafa'
                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(IconButton, { onClick: onClose, size: "small", disableRipple: true, sx: { p: 0, '&:hover': { backgroundColor: 'transparent' } }, children: _jsx(CloseIcon, { sx: { fontSize: 20, color: '#71717A' } }) }), _jsx("div", { className: "line" }), _jsx(Typography, { sx: {
                                                    fontSize: '16px',
                                                    fontWeight: 500,
                                                    color: '#18181b',
                                                    lineHeight: '24px'
                                                }, className: "drawer-main-title", children: isEditMode ? "Edit User" : "Add New User" })] }), _jsx(AppButton, { variant: "contained", onClick: () => formik.handleSubmit(), startIcon: _jsx(SaveIcon, {}), disabled: formik.isSubmitting, color: "primary", className: "app-common-button", children: formik.isSubmitting ? _jsx(CustomLoader, { sx: { color: 'white' }, size: 18 }) : 'Save' })] }), _jsxs(Box, { className: "form-input-root gap-responsive", component: "form", onSubmit: formik.handleSubmit, sx: {
                                    flex: 1,
                                    overflowY: 'auto',
                                    padding: '20px',
                                    paddingBottom: '100px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '20px'
                                }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '10px' }, children: [_jsx(Typography, { sx: {
                                                            fontSize: '18px',
                                                            fontWeight: 600,
                                                            color: '#ff008e',
                                                            lineHeight: '28px'
                                                        }, className: "field-drawer-title", children: "Basic Information" }), _jsx(Box, { sx: { height: '1px', backgroundColor: '#e9eaeb', width: '100%' } })] }), _jsxs(Box, { sx: { display: 'flex', gap: '16px', alignItems: 'center' }, children: [_jsx("input", { type: "file", accept: "image/*", style: { display: "none" }, id: "avatar-upload", onChange: (e) => handleAvatarUpload(e, formik.setFieldError) }), _jsx(Avatar, { sx: { width: 80, height: 80, fontSize: "28px", fontWeight: 500 }, src: getAvatarSrc(formik.values.avatar), children: !formik.values.avatar && typeof avatarInitials === 'string' ? avatarInitials : null }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: [_jsx(Button, { component: "label", htmlFor: "avatar-upload", variant: "outlined", size: "small", startIcon: _jsx(EditIcon, { fontSize: "small" }), sx: {
                                                                    color: '#374151',
                                                                    borderColor: '#D0D5DD',
                                                                    fontSize: '13px',
                                                                    fontWeight: 500,
                                                                    textTransform: 'none',
                                                                    '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
                                                                }, children: "Edit Avatar" }), formik.values.avatar && (_jsx(Button, { variant: "text", size: "small", onClick: () => {
                                                                    formik.setFieldValue('avatar', '');
                                                                    setAvatarInitials(null);
                                                                    const fileInput = document.getElementById('avatar-upload');
                                                                    if (fileInput)
                                                                        fileInput.value = '';
                                                                }, sx: {
                                                                    color: '#EF4444',
                                                                    fontSize: '13px',
                                                                    fontWeight: 500,
                                                                    textTransform: 'none',
                                                                    justifyContent: 'flex-start',
                                                                    p: 0,
                                                                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                                                                }, children: "Remove" }))] })] }), formik.errors.avatar && (_jsx(Typography, { sx: { color: "red", fontSize: "12px", mt: 1 }, children: formik.errors.avatar })), _jsxs(Box, { sx: { display: 'flex', gap: '20px' }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(TextInput, { label: 'First Name', type: 'text', required: true, value: formik.values.firstName, placeholder: 'Enter your First Name', onChange: (e) => handleChange('firstName', e), onBlur: () => handleBlur('firstName'), autoComplete: 'off', error: !!formik.errors.firstName && !!formik.touched.firstName, errorMessage: typeof formik.errors.firstName === 'string' ? formik.errors.firstName : undefined }) }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(TextInput, { placeholder: 'Enter your Last Name', label: 'Last Name', type: 'text', required: true, value: formik.values.lastName, onChange: (e) => handleChange('lastName', e), onBlur: () => handleBlur('lastName'), autoComplete: 'off', error: !!formik.errors.lastName && !!formik.touched.lastName, errorMessage: typeof formik.errors.lastName === 'string' ? formik.errors.lastName : undefined }) })] }), _jsxs(Box, { sx: { display: 'flex', gap: '20px' }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(EmailInput, { label: "Email Address", placeholder: "Enter email address", value: formik.values.email, onChange: (e) => handleChange('email', e), onBlur: () => handleBlur('email'), autoComplete: "off", error: !!emailError || (!!formik.errors.email && !!formik.touched.email), errorMessage: emailError || (typeof formik.errors.email === 'string' ? formik.errors.email : undefined), maxLength: 50 }) }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(Dropdown, { label: "Role", value: formik.values.role, onChange: (val) => handleRoleChange('role', { target: { value: val } }), error: !!formik.errors.role && !!formik.touched.role, errorMessage: typeof formik.errors.role === 'string' ? formik.errors.role : undefined, options: roleOptions, placeholder: "Select Role", searchable: true, required: true, rightIcon: isEditMode ? (_jsx("span", { onClick: (e) => { e.stopPropagation(); setOverrideDrawerOpen(true); }, style: { cursor: "pointer", color: hasOverrides ? "#2e90fa" : "inherit" }, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg", style: { marginLeft: 8 }, children: _jsx("path", { d: "M3 1.5v6m0 0A1.5 1.5 0 1 0 4.5 9M3 7.5A1.5 1.5 0 0 1 4.5 9M9 4.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3m0 0A4.5 4.5 0 0 1 4.5 9", stroke: hasOverrides ? "#2e90fa" : "#667085", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }) })) : undefined }) })] })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '10px' }, children: [_jsx(Typography, { sx: {
                                                            fontSize: '18px',
                                                            fontWeight: 600,
                                                            color: '#ff008e',
                                                            lineHeight: '28px'
                                                        }, className: "field-drawer-title", children: "Other" }), _jsx(Box, { sx: { height: '1px', backgroundColor: '#e9eaeb', width: '100%' } })] }), _jsxs(Box, { sx: { display: 'flex', gap: '20px' }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(Dropdown, { label: "Designation", value: formik.values.user_type, onChange: (val) => handleChange('user_type', { target: { value: val } }), error: !!formik.errors.user_type && !!formik.touched.user_type, errorMessage: typeof formik.errors.user_type === 'string' ? formik.errors.user_type : undefined, options: designationOptions, placeholder: "Select Designation", multiple: true, searchable: true, required: true }) }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(Dropdown, { label: "Location", value: formik.values.location, onChange: (val) => handleChange('location', { target: { value: val } }), error: !!formik.errors.location && !!formik.touched.location, errorMessage: typeof formik.errors.location === 'string' ? formik.errors.location : undefined, options: locationOptions, placeholder: "Select Location", required: true }) })] }), _jsxs(Box, { sx: { display: 'flex', gap: '20px' }, children: [_jsx(Box, { sx: { flex: 1 }, children: (() => {
                                                            const currentUserId = getCurrentUserId();
                                                            const editingUserId = userData?.id || userData?._id;
                                                            const isSelf = isEditMode && currentUserId && currentUserId === editingUserId;
                                                            // Filter out "Inactive" option if user is editing themselves
                                                            const filteredStatusOptions = isSelf
                                                                ? statusOptions.filter(opt => opt.value === "Active")
                                                                : statusOptions;
                                                            return (_jsx(Dropdown, { label: "Status", value: formik.values.status, onChange: (val) => handleChange('status', { target: { value: val } }), error: !!formik.errors.status && !!formik.touched.status, errorMessage: typeof formik.errors.status === 'string' ? formik.errors.status : undefined, options: filteredStatusOptions, placeholder: "Select Status", required: true }));
                                                        })() }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(Dropdown, { label: "Two-Factor Authentication (2FA)", value: formik.values.two_step_enabled, onChange: (val) => formik.setFieldValue('two_step_enabled', val), error: !!formik.errors.two_step_enabled && !!formik.touched.two_step_enabled, errorMessage: typeof formik.errors.two_step_enabled === 'string' ? formik.errors.two_step_enabled : undefined, options: twoFactorAuth, placeholder: "Choose your 2FA method", required: true }) })] }), isEditMode && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-end', gap: '12px' }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(TextInput, { label: "Clockify ID", value: clockifyId, placeholder: "Not synced yet", disabled: true, readOnly: true, onChange: () => { } }) }), !clockifyId && (_jsx(Button, { variant: "outlined", onClick: handleSyncClockifyId, disabled: clockifySyncing, title: "Sync Clockify ID (matches the Clockify account by email)", disableFocusRipple: true, sx: {
                                                            minWidth: 0, width: '44px', height: '44px', p: 0, borderRadius: '8px',
                                                            color: '#344054', borderColor: '#D0D5DD', textTransform: 'none',
                                                            '&:hover, &.Mui-focusVisible': { bgcolor: '#F9FAFB', borderColor: '#D0D5DD' },
                                                            // "&&" beats App.css's global pink fill for disabled primary buttons.
                                                            '&&.Mui-disabled': { color: '#667085', borderColor: '#E9EAEB', backgroundColor: '#FAFAFA', cursor: 'progress', pointerEvents: 'auto' },
                                                        }, children: clockifySyncing ? _jsx(CircularProgress, { size: 18, thickness: 5, sx: { color: '#667085' } }) : _jsx(RefreshCw, { size: 18 }) }))] })), _jsx(FormControlLabel, { control: _jsx(Checkbox, { size: "small", checked: !!formik.values.is_meeting_am, onChange: (e) => formik.setFieldValue('is_meeting_am', e.target.checked), sx: { color: '#D0D5DD', '&.Mui-checked': { color: '#FF008E' } } }), label: _jsx(Typography, { sx: { fontSize: 14, fontWeight: 500, color: '#101828' }, children: "Meeting AM" }), sx: { m: 0 } }), _jsx("div", { className: "selected-list-tags", children: _jsx(Dropdown, { label: "Employee Portfolio", value: formik.values.employeePortfolio, onChange: (val) => formik.setFieldValue('employeePortfolio', val), error: !!formik.errors.employeePortfolio && !!formik.touched.employeePortfolio, errorMessage: typeof formik.errors.employeePortfolio === 'string' ? formik.errors.employeePortfolio : undefined, options: employeePortfolioOptions, placeholder: "Select Employee Portfolio", multiple: true, usePortal: true }) }), isEditMode && (userData?.id || userData?._id) && (_jsx(UserAssignedClients, { userId: String(userData?.id || userData?._id) }))] })] })] }) }) }), _jsx(OverrideRoleDrawer, { type: 'override-popup-container', open: overrideDrawerOpen, onClose: () => setOverrideDrawerOpen(false), roleName: getRoleName(formik.values.role), userId: userData?.id || userData?._id, initialPermissions: getRolePermissionsById(formik.values.role) ? transformUserPermissions(getRolePermissionsById(formik.values.role)) : undefined, initialOverrides: userData?.overrides ? transformUserOverrides(userData.overrides) : undefined, onSave: (data) => {
                    // Update hasOverrides state based on saved overrides
                    const savedOverrides = data.overrides || {};
                    const hasOverridesData = Object.keys(savedOverrides).length > 0;
                    setHasOverrides(hasOverridesData);
                    // Notify parent component (UserList) about override save
                    if (onOverrideSave && data.overrides) {
                        onOverrideSave(data.overrides, data.finalPermissions);
                    }
                } }), _jsx(ConfirmActionModal, { open: showRoleChangeModal, title: "Override Permissions", description: "This user currently has custom permissions. Changing the role will remove these permissions and apply the default permissions for the new role. Do you want to continue?", confirmText: "Yes, Apply New Role", cancelText: "No, Cancel", onConfirm: () => {
                    if (!pendingRole)
                        return;
                    formik.setFieldValue('role', pendingRole);
                    const newRolePermissions = getRolePermissionsById(pendingRole);
                    if (userData) {
                        userData.permissions = newRolePermissions;
                        userData.overrides = {};
                    }
                    setHasOverrides(false);
                    if (onOverrideSave) {
                        onOverrideSave({});
                    }
                    setPreviousRole(pendingRole);
                    setShowRoleChangeModal(false);
                    setPendingRole(null);
                }, onClose: () => {
                    setShowRoleChangeModal(false);
                    setPendingRole(null);
                } })] }));
}
export default React.memo(UserDrawer);
